import { useEffect, useState } from 'react';
import {
  Zap,
  Activity,
  Clock,
  ShieldAlert,
  AlertTriangle,
  Database,
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Info,
  Download,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStockSyncOverview } from '../hooks/useStockSync';
import { MetricCard } from '@/features/production-orders/components/metrics/MetricCard';
import { PushConfirmDialog } from './PushConfirmDialog';
import { ReconcilePanel } from './ReconcilePanel';
import { SyncFlowDiagram } from './SyncFlowDiagram';
import { OrderSyncCard } from './OrderSyncCard';
import { PapSyncCard } from './PapSyncCard';
import { useOrganization } from '@/contexts/OrganizationContext';

export function StockSyncOverview({ onNavigateApprovals }: { onNavigateApprovals: () => void }) {
  const { t } = useTranslation('inventory');
  const { organization } = useOrganization();
  const { data, isLoading, isFetching, refetch } = useStockSyncOverview();
  const utils = trpc.useUtils();

  // Order sync state for flow diagram health
  const orderSyncState = trpc.shopifyIntegration.getOrderSyncState.useQuery(undefined, {
    refetchInterval: 30_000,
  });

  const [showConfirm, setShowConfirm] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState<{
    expectedOrganizationId: string;
    goingToZero: Array<{ sku: string; previousQty: number; newQty: number }>;
    totalChanges: number;
    /** SKUs set to Always 0 that the push writes as 0 (not in goingToZero). */
    alwaysZeroCount?: number;
  } | null>(null);

  // Keep the confirmation bound to the tenant whose zero-stock preview the
  // operator reviewed. Organization switches invalidate the open dialog.
  useEffect(() => {
    if (previewData && previewData.expectedOrganizationId !== organization?.id) {
      setShowConfirm(false);
      setPreviewData(null);
    }
  }, [organization?.id, previewData]);

  const triggerPush = trpc.organization.triggerInventoryPush.useMutation({
    onSuccess: (result) => {
      if (result.success) {
        const total = result.papUpdated + result.podUpdated;
        const prefix = result.dryRun ? '[DRY RUN] ' : '';
        if (total === 0) {
          if (result.podSkipped > 0) {
            toast.warning(`${prefix}${result.podSkipped} On-demand SKU(s) had no matching Shopify variants`, {
              description: 'Check that these products exist in your Shopify store',
              duration: 6000,
            });
          } else {
            toast.info(`${prefix}No changes to push: all quantities match Shopify`);
          }
        } else {
          const activatedNote = result.activated ? ` (${result.activated} activated)` : '';
          const skippedNote = result.podSkipped > 0 ? `, ${result.podSkipped} on-demand had no variants` : '';
          toast.success(`${prefix}Push complete: ${result.papUpdated} pre-made, ${result.podUpdated} on-demand updated${activatedNote}${skippedNote}`);
        }
      } else {
        toast.error(`Push failed with ${result.errors} error(s)`, {
          description: result.shopifyErrors?.[0],
          duration: 8000,
        });
      }
      utils.stockSync.getOverview.invalidate();
    },
    onError: (error) => toast.error(`Push failed: ${error.message}`),
  });

  const backfillMutation = trpc.organization.backfillInventory.useMutation({
    onSuccess: (result) => {
      toast.success(`Linked ${result.updatedCount} SKUs (${result.remaining} remaining)`);
      utils.stockSync.getOverview.invalidate();
    },
    onError: (error) => toast.error(`Backfill failed: ${error.message}`),
  });

  const approveHalt = trpc.organization.approveHaltedPush.useMutation({
    onSuccess: (result) => {
      if (result.errors > 0) {
        toast.error(`Halt approved but push had ${result.errors} error(s)`, {
          description: result.shopifyErrors?.[0],
          duration: 8000,
        });
      } else {
        const total = result.papUpdated + result.podUpdated;
        if (total === 0 && result.podSkipped > 0) {
          toast.warning(`Halt approved: ${result.podSkipped} on-demand SKU(s) had no matching Shopify variants`, {
            description: 'Check that these products exist in your Shopify store',
            duration: 6000,
          });
        } else {
          const activatedNote = result.activated ? ` (${result.activated} activated)` : '';
          const skippedNote = result.podSkipped > 0 ? `, ${result.podSkipped} on-demand had no variants` : '';
          toast.success(`Halt approved: ${result.papUpdated} pre-made, ${result.podUpdated} on-demand updated${activatedNote}${skippedNote}`);
        }
      }
      utils.stockSync.getOverview.invalidate();
      utils.organization.getOrganization.invalidate();
    },
    onError: (error) => toast.error(`Failed: ${error.message}`),
  });

  const dismissHalt = trpc.organization.dismissHaltedPush.useMutation({
    onSuccess: (result) => {
      // The server keeps the halt when it cannot read the org (it fails
      // closed, so nothing is marked). Say so and leave the banner as it is:
      // it reads the halt from the server, which still has it.
      if (!result.success) {
        toast.error(t('stockSync.overview.dismissFailed'), { duration: Infinity });
        return;
      }
      toast.success('Halt dismissed, acknowledged without pushing');
      utils.stockSync.getOverview.invalidate();
      utils.organization.getOrganization.invalidate();
    },
    onError: (error) => toast.error(`Failed: ${error.message}`),
  });

  const handlePushNow = async () => {
    if (!organization?.id) {
      toast.error(t('push.organizationUnavailable'));
      return;
    }
    const expectedOrganizationId = organization.id;
    setIsPreviewing(true);
    try {
      const preview = await utils.organization.previewInventoryPush.fetch({
        expectedOrganizationId,
      });
      if (preview.goingToZero.length > 0) {
        setPreviewData({ ...preview, expectedOrganizationId });
        setShowConfirm(true);
      } else {
        triggerPush.mutate({ expectedOrganizationId });
      }
    } catch {
      // Preview failed — warn the user instead of silently pushing
      toast.error('Could not load push preview, please try again', {
        description: 'The safety check for zero-stock SKUs was unavailable.',
        duration: 6000,
      });
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleConfirmPush = () => {
    if (!previewData) return;
    triggerPush.mutate({ expectedOrganizationId: previewData.expectedOrganizationId }, {
      onSettled: () => {
        setShowConfirm(false);
        setPreviewData(null);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Stat card skeletons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
        </div>
        {/* Sync flow diagram skeleton */}
        <Skeleton className="h-48 rounded-lg" />
        {/* Inbound sync card skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-40 rounded-lg" />
          <Skeleton className="h-40 rounded-lg" />
        </div>
      </div>
    );
  }

  const overview = data;

  const lastPushDisplay = overview?.lastPushAt
    ? formatDistanceToNow(new Date(overview.lastPushAt), { addSuffix: true })
    : 'Never';

  const lastPushDetail = overview?.lastPushAt
    ? new Date(overview.lastPushAt).toLocaleDateString()
    : '';

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard
          variant="production"
          icon={Zap}
          title="Pushes Today"
          value={`${overview?.pushesToday ?? 0}`}
          subtitle={`${overview?.papPushesToday ?? 0} pre-made / ${overview?.podPushesToday ?? 0} on-demand`}
        />
        <MetricCard
          variant="info"
          icon={Activity}
          title="Variants Updated"
          value={`${overview?.variantsUpdatedToday ?? 0}`}
          subtitle="last 24h"
        />
        <MetricCard
          variant="gst"
          icon={Database}
          title="SKUs Tracked"
          value={`${overview?.trackedSkus ?? 0}`}
          subtitle="in push state"
        />
        <MetricCard
          variant="shipping"
          icon={Clock}
          title="Last Push"
          value={lastPushDisplay}
          subtitle={lastPushDetail}
        />
      </div>

      {/* Empty state guidance */}
      {(overview?.pushesToday ?? 0) === 0 && !overview?.pushEnabled && (
        <Alert className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm">
            Enable inventory push in Configuration to get started.
          </AlertDescription>
        </Alert>
      )}

      {/* Pending approvals banner */}
      {(overview?.pendingCount ?? 0) > 0 && (
        <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              <span className="font-medium">{overview?.pendingCount} stock change{overview?.pendingCount !== 1 ? 's' : ''}</span>
              {' '}held for approval: significant drops detected before pushing to Shopify.
            </span>
            <Button variant="outline" size="sm" onClick={onNavigateApprovals}>
              Review
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Product lifecycle discovery found active Shopify products, but their
          complete variant set has not crossed the Product Sync approval seam. */}
      {(overview?.needsProductSyncCount ?? 0) > 0 && (
        <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              <span className="font-medium">
                {t('stockSync.overview.needsProductSync', {
                  count: overview?.needsProductSyncCount ?? 0,
                })}
              </span>
              {' '}{t('stockSync.overview.needsProductSyncBody')}
            </span>
            <Button asChild variant="outline" size="sm">
              <Link to="/product-sync">{t('stockSync.overview.openProductSync')}</Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Halt banner */}
      {overview?.halted && (
        <Alert variant="destructive" className="border-red-400 bg-red-50 dark:bg-red-950/30">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-3">
              <div>
                <p className="font-medium text-red-800 dark:text-red-300">
                  Inventory push paused: mass out-of-stock detected
                </p>
                <p className="text-sm mt-1">{overview.halted.reason}</p>
              </div>
              {overview.halted.skuSamples && overview.halted.skuSamples.length > 0 && (
                <div className="rounded border border-red-200 dark:border-red-800 p-2 text-xs space-y-1">
                  {overview.halted.skuSamples.slice(0, 5).map((s, i) => (
                    <p key={i} className="font-mono">
                      {s.sku}: {s.previousQty} → {s.newQty}
                    </p>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => approveHalt.mutate()}
                  disabled={approveHalt.isPending || dismissHalt.isPending}
                >
                  {approveHalt.isPending
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Pushing...</>
                    : <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Approve &amp; Push</>}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => dismissHalt.mutate()}
                  disabled={approveHalt.isPending || dismissHalt.isPending}
                >
                  {dismissHalt.isPending
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Dismissing...</>
                    : <><XCircle className="h-3.5 w-3.5 mr-1.5" />Dismiss</>}
                </Button>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* BLU-495 Q4: Back-sync halt banner removed. The push worker's existing
          `halted` banner (above) is the single source of halt visibility. */}

      {/* Sync flow diagram */}
      <SyncFlowDiagram
        orderSyncLastAt={orderSyncState.data?.last_completed_at}
        pushEnabled={overview?.pushEnabled}
        pushHealthy={overview?.pushEnabled && !overview?.halted}
      />

      {/* SKU linking alert */}
      {(overview?.missingInventoryItemIds ?? 0) > 0 && (
        <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
          <div className="flex items-center gap-3 min-w-0">
            <Database className="h-4 w-4 text-muted-foreground shrink-0" />
            <p className="text-sm">
              <span className="font-medium">{overview?.missingInventoryItemIds} pre-made SKUs</span>{' '}
              are missing Shopify inventory item IDs and cannot be pushed until linked.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => backfillMutation.mutate({ batchSize: 10 })}
            disabled={backfillMutation.isPending}
            className="shrink-0"
          >
            {backfillMutation.isPending
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Linking...</>
              : 'Link SKUs'}
          </Button>
        </div>
      )}

      {/* Quick actions */}
      <Card className="bg-muted/30">
        <CardContent className="flex items-center gap-4 py-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePushNow}
            disabled={triggerPush.isPending || isPreviewing || !overview?.pushEnabled}
            aria-label="Push inventory to Shopify"
          >
            {triggerPush.isPending || isPreviewing
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />{isPreviewing ? 'Checking...' : 'Pushing...'}</>
              : <><Zap className="h-3.5 w-3.5 mr-1.5" />Push Now</>}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <div className="flex-1" />
          <div className="flex gap-2">
            {overview?.pushEnabled ? (
              <>
                {overview?.dryRunMode && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">Dry Run</Badge>
                )}
                {overview?.autoSyncEnabled ? (
                  <Badge variant="outline" className="text-green-600 border-green-300">Auto Sync</Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">Manual Only</Badge>
                )}
                {overview?.hybridQty && (
                  <Badge variant="outline" className="text-blue-600 border-blue-300">Hybrid Mode</Badge>
                )}
              </>
            ) : (
              <Badge variant="secondary">Push Disabled</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reconcile & force-push panel (BLU-550 / 58-05) — its own card, after Quick actions */}
      <ReconcilePanel />

      {/* Inbound sync cards */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Download className="h-4 w-4" />
          Shopify Import
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <OrderSyncCard />
          <PapSyncCard />
        </div>
      </div>

      <PushConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        onConfirm={handleConfirmPush}
        goingToZero={previewData?.goingToZero ?? []}
        totalChanges={previewData?.totalChanges ?? 0}
        alwaysZeroCount={previewData?.alwaysZeroCount ?? 0}
        isPending={triggerPush.isPending}
        mode="bulk"
        isDryRun={overview?.dryRunMode}
        hybridQtyEnabled={overview?.hybridQty}
      />
    </div>
  );
}
